import { ArrowRight, CheckCircle2, CircleHelp, FileUp, GitBranch, Lightbulb, Search, Sparkles } from "lucide-react";

type UserGuidePanelProps = {
  onStartCapture: () => void;
};

const steps = [
  {
    title: "1. Tạo nguồn học",
    body: "Bấm nút + bên trái để tạo khóa học, sách, bài viết hoặc chủ đề. Nếu có module/bài học, chọn parent để tạo cây cha con.",
    hint: "Ví dụ: Course 'Khoa AI Automation' -> Module 'Ontology' -> Lesson 'Relation model'."
  },
  {
    title: "2. Ghi nhanh điều vừa học",
    body: "Chọn nguồn học, chọn Note, Task, Quote hoặc Practice, rồi ghi thẳng nội dung vào Ghi nhanh.",
    hint: "Chưa cần đúng cấu trúc ngay. Cứ ghi trước, sắp xếp sau."
  },
  {
    title: "3. Nối các ý liên quan",
    body: "Sau khi note được tạo, bấm Connect để nối note với source, note khác, task hoặc concept.",
    hint: "Dùng mentions, supports, contradicts, turns_into_task, enriches hoặc belongs_to."
  },
  {
    title: "4. Lưu link khi cần truy xuất lại",
    body: "Nếu note hoặc source có trang web, bài viết, video hoặc tài liệu gốc, bấm Links và dán URL vào.",
    hint: "Link được lưu theo từng thực thể, mở lại bằng nút Links trên source hoặc note."
  },
  {
    title: "5. Để AI gợi ý, mình quyết định",
    body: "Bấm Suggest trên note, xem gợi ý trong AI review queue, sau đó Accept nếu đúng hoặc Reject nếu sai.",
    hint: "AI không tự ý sửa note. Mọi thay đổi quan trọng đều cần bạn duyệt."
  }
];

const examples = [
  {
    label: "Đang học khóa học",
    text: "Tạo course và module trước, mỗi buổi học capture note/practice vào lesson tương ứng."
  },
  {
    label: "Đang đọc sách",
    text: "Tạo source loại book, capture Quote cho câu hay, Note cho suy nghĩ, Task cho việc muốn làm."
  },
  {
    label: "Đang đọc bài viết",
    text: "Tạo source loại article, lưu link bài viết, ghi ý chính, rồi Connect với topic hoặc note đã có."
  }
];

export function UserGuidePanel({ onStartCapture }: UserGuidePanelProps) {
  return (
    <div className="space-y-4">
      <section className="rounded-lg border border-line bg-white shadow-panel">
        <div className="border-b border-line px-4 py-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="mb-2 inline-flex h-8 items-center gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 text-xs font-bold text-brand">
                <CircleHelp size={15} aria-hidden="true" />
                Hướng dẫn cho người mới
              </div>
              <h1 className="text-xl font-bold leading-tight text-ink">Dùng Human Note như một sổ tay học tập có liên kết</h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                Bạn chỉ cần ghi lại điều vừa học trước. Sau đó mới gắn vào nguồn học, lưu link gốc,
                tách thành task/quote/practice, nối relation và để AI gợi ý thêm.
              </p>
            </div>
            <button
              className="inline-flex h-10 shrink-0 items-center gap-2 rounded-lg bg-brand px-4 text-sm font-bold text-white transition hover:bg-blue-700"
              type="button"
              onClick={onStartCapture}
            >
              Bắt đầu ghi note
              <ArrowRight size={17} aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="grid gap-3 p-4 md:grid-cols-2">
          {steps.map((step) => (
            <article key={step.title} className="rounded-lg border border-line bg-slate-50 p-4">
              <h2 className="text-sm font-bold text-ink">{step.title}</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">{step.body}</p>
              <p className="mt-3 rounded-md border border-orange-100 bg-orange-50 px-3 py-2 text-xs font-semibold leading-5 text-focus">
                {step.hint}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(300px,0.9fr)]">
        <div className="rounded-lg border border-line bg-white shadow-panel">
          <div className="border-b border-line px-4 py-3">
            <h2 className="text-sm font-bold">Một ngày sử dụng đơn giản</h2>
          </div>
          <div className="divide-y divide-line px-4">
            <GuideRow icon={CheckCircle2} title="Mở source đang học" body="Chọn course, book, article hoặc topic bên trái. Nếu chưa có, tạo mới bằng nút +." />
            <GuideRow icon={Lightbulb} title="Capture ý mới" body="Ghi đúng ngôn ngữ của bạn. Note lưu theo nội dung bạn viết." />
            <GuideRow icon={GitBranch} title="Connect khi thấy liên quan" body="Nối note với ý cũ, task mới, quote, source hoặc concept để graph có ý nghĩa." />
            <GuideRow icon={FileUp} title="Gắn link hoặc file" body="Dùng Links để lưu URL, dùng Files để lưu PDF, ảnh hoặc tài liệu liên quan." />
            <GuideRow icon={Sparkles} title="Review gợi ý" body="Dùng Suggest khi cần AI bóc tách task, relation hoặc concept. Chỉ Accept khi thấy đúng." />
            <GuideRow icon={Search} title="Tìm lại khi cần" body="Nhập từ khóa trên thanh search để tìm note, source, task hoặc concept đã lưu." />
          </div>
        </div>

        <div className="rounded-lg border border-line bg-white shadow-panel">
          <div className="border-b border-line px-4 py-3">
            <h2 className="text-sm font-bold">Nên chọn loại nào?</h2>
          </div>
          <div className="space-y-3 p-4">
            <TypeTip label="Note" body="Tóm tắt, suy nghĩ, ý tưởng, điều vừa hiểu ra." />
            <TypeTip label="Task" body="Việc cần làm sau, câu hỏi cần trả lời, bước tiếp theo." />
            <TypeTip label="Quote" body="Câu trích dẫn từ sách, bài viết, video, khóa học." />
            <TypeTip label="Practice" body="Bài tập, việc cần thực hành, thử nghiệm cần lặp lại." />
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-line bg-white shadow-panel">
        <div className="border-b border-line px-4 py-3">
          <h2 className="text-sm font-bold">Ví dụ gắn với cách học thật</h2>
        </div>
        <div className="grid gap-3 p-4 md:grid-cols-3">
          {examples.map((example) => (
            <article key={example.label} className="rounded-lg border border-line bg-white p-4">
              <h3 className="text-sm font-bold text-ink">{example.label}</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">{example.text}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function GuideRow({ icon: Icon, title, body }: { icon: typeof CheckCircle2; title: string; body: string }) {
  return (
    <div className="flex gap-3 py-4">
      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-line bg-slate-50 text-brand">
        <Icon size={17} aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <h3 className="text-sm font-bold text-ink">{title}</h3>
        <p className="mt-1 text-sm leading-6 text-slate-600">{body}</p>
      </div>
    </div>
  );
}

function TypeTip({ label, body }: { label: string; body: string }) {
  return (
    <div className="rounded-lg border border-line bg-slate-50 px-3 py-3">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-7 min-w-20 items-center justify-center rounded-md border border-blue-100 bg-blue-50 px-2 text-xs font-bold text-brand">
          {label}
        </span>
        <p className="text-sm leading-6 text-slate-600">{body}</p>
      </div>
    </div>
  );
}
